import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react'
import { selectConfig } from '../reducers/auth';
import { IConfig } from '../model/IConfig';
import { RootState } from '../store/store'
import { IMeasure } from '../model/IMeasure';
import { Point } from 'geojson';

function dynamicBaseQueryBackend(): BaseQueryFn<string | FetchArgs,unknown,FetchBaseQueryError> {
  return async (args: any, api, extraOptions) => {
    const config: IConfig | null = selectConfig(api.getState() as RootState)
    // gracefully handle scenarios where data to generate the URL is missing
    if (!config) {
      return {
        error: {
          status: 400,
          statusText: 'Bad Request',
          data: 'No config received',
        },
      }
    }

    const rawBaseQuery = fetchBaseQuery({
      baseUrl: config.backendUrl, 
      prepareHeaders: (headers, { getState }) => {
        if (!args.url?.endsWith("Login")){
          const token = (getState() as any).auth.user.token;
           if (token) {
            headers.set('authorization', `Bearer ${token}`);
          }
          /* else{
            const token1 = getTokenFromLocalStorage(); 
            if (token1) {
              headers.set('authorization', `Bearer ${token}`);
            }
          }     */  
         
          return headers;
        }        
      },      
    }) 

    const result = await rawBaseQuery(args, api, extraOptions)    
    return result
  }
}  

export interface ILoginPayload {
  login: string;
  password: string;
}

interface ILoginResponse {
  token: string;
}

export interface IWorkingAreaResponse {
    id: string;
    name: string;
    sourceTableName: string;
    isPolygonEditable: boolean;
    geometry: string;
  }

  export interface IRiverResponse {
    id: string;
    name: string;
    geometry: string;
  }

export interface IGetMeasure {
  id: number;
  ownedByUser: boolean;
  geometry: string;
  createdInCycle: number;
  updatedInCycle?: number;
  description: string; 
  sourceTableName?: string;
  name: string; 
  measureType: string;
  euAspect?: number;
  actionField?: number;
  implementationStatus: string;
  emailAddress: string;
  point?: Point;
  measureId?: number;
  updatedAt?: string;
  projectDeveloper?: string;
  createdBy: string;
  updatedBy: string;
}

export interface IMeasureLocation {
  x: number;
  y: number;
  sourceTableName: string;
}

export interface ICheckMeasureLocationResponse{
  hasAccess: boolean;
}

export interface IGetNearestMeasureId {
  measureId: number;
}

export interface IGetNearestMeasureIdPayload {
  x: number;
  y: number;
  radius: number;
}

export interface IGetProjectDevelopersResponse {
  projectDevelopers: Array<string>;
}

const BASE_URL = "Measure"

export const backendApi = createApi({
  reducerPath: "backendApi",
  baseQuery: dynamicBaseQueryBackend(),
  tagTypes: ['Measures'],
  endpoints: build => ({
    login: build.mutation<ILoginResponse, ILoginPayload>({
      query(body: ILoginPayload) {
        return {
          url: `api/Auth/Login`,
          method: 'POST',
          body,
        }
      },       
    }),
    getNearestMeasureId: build.query<IGetNearestMeasureId, IGetNearestMeasureIdPayload>({
      query: (body: IGetNearestMeasureIdPayload) => {      
        return `api/Measure/GetNearestMeasureId?x=${body.x}&y=${body.y}&radius=${body.radius}`
      }      
    }), 
    getProjectDevelopers: build.query<IGetProjectDevelopersResponse, void>({
      query: () => {      
        return `api/Measure/GetAvailableProjectDevelopers`
      }      
    }),    
    getRiver: build.query<Array<IRiverResponse>, void>({
      query: () => {      
        return `api/River`
      }      
    }),    
    getWorkingArea: build.query<Array<IWorkingAreaResponse>, void>({
      query: () => {      
        return `api/Workingarea`
      }      
    }),    
    getMeasures: build.query<Array<IGetMeasure>, null>({
      query: ( ) => {        
        return `api/${BASE_URL}`
      },
      providesTags: (result) =>
        result
          ? // successful query
            [
              ...result.map(({ id }) => ({ type: 'Measures', id }) as const),
              { type: 'Measures', id: 'LIST' },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Posts', id: 'LIST' }` is invalidated
            [{ type: 'Measures', id: 'LIST' }]
      
    }),
    checkMeasure: build.query< ICheckMeasureLocationResponse,IMeasureLocation>({
      query: (body: IMeasureLocation ) => {        
        return `api/${BASE_URL}/CheckUserAccess?x=${body.x}&y=${body.y}&sourceTableName=${body.sourceTableName}`
      }      
    }),
    createMeasure: build.mutation<IMeasure, Partial<IMeasure>>({
      query(body) {
        return {
          url: `api/${BASE_URL}`,
          method: 'POST',
          body,
        }
      }, 
      invalidatesTags: [{ type: 'Measures', id: 'LIST' }],   
    }),
     editMeasure: build.mutation<IMeasure, Partial<IMeasure>>({
      query(body: IMeasure) {
        return {
          url: `api/${BASE_URL}?measureId=${body.measureId}`,
          method: 'PUT',
          body
        }
      }, 
      invalidatesTags: [{ type: 'Measures', id: 'LIST' }],   
    })
  })
})

export const {useLoginMutation, useCreateMeasureMutation, useGetMeasuresQuery, useGetWorkingAreaQuery, useEditMeasureMutation, useCheckMeasureQuery, useGetRiverQuery, useGetNearestMeasureIdQuery, useGetProjectDevelopersQuery} = backendApi
